import { type GeneratedItem } from "@/features/Generations/types/generatedItem";
import {
  ITEM_STATUS_FAILED,
  type PropagationItem,
  type PublishAssetInput,
} from "@/types/propagation";

/**
 * Stable id for a propagation item / its grid tile: the backend `slug`, then the
 * legacy `id`, then a 1-based padded sequence. Shared so the grid tiles and the
 * publish payload key items the same way (accepted ids round-trip cleanly).
 */
export function propagationItemId(
  item: PropagationItem,
  index: number,
): string {
  return item.slug || item.id || String(index + 1).padStart(3, "0");
}

export function propagationItemsToGeneratedItems(
  items: PropagationItem[],
): GeneratedItem[] {
  return items.map((item, index) => {
    const imageUrl = item.generation?.assets[0]?.url;
    const failed = item.status === ITEM_STATUS_FAILED;
    return {
      id: propagationItemId(item, index),
      sequence: String(index + 1).padStart(3, "0"),
      sku:
        item.generation?.name?.trim() ||
        item.product_title?.trim() ||
        item.generation_id ||
        String(index + 1).padStart(3, "0"),
      status: failed ? "failed" : "generated",
      accepted: item.is_accepted,
      ...(imageUrl ? { imageUrl } : {}),
      ...(failed ? { failureReason: "This item failed to generate." } : {}),
    } satisfies GeneratedItem;
  });
}

/** Accepted items with a rendered asset: exactly what gets published. */
function publishableItems(
  items: PropagationItem[],
  acceptedIds: readonly string[],
): PropagationItem[] {
  const accepted = new Set(acceptedIds);
  return items.filter(
    (item, index) =>
      accepted.has(propagationItemId(item, index)) &&
      Boolean(item.generation?.assets[0]?.url),
  );
}

/**
 * Publishable items with no product identity. The publish API rejects the
 * whole request if any asset lacks a vendor_product_id.
 */
export function publishableItemsMissingProduct(
  items: PropagationItem[],
  acceptedIds: readonly string[],
): PropagationItem[] {
  return publishableItems(items, acceptedIds).filter(
    (item) => !item.vendor_product_id,
  );
}

export function propagationItemsToPublishAssets(
  items: PropagationItem[],
  acceptedIds: readonly string[],
): PublishAssetInput[] {
  const publishAssets: PublishAssetInput[] = [];
  publishableItems(items, acceptedIds).forEach((item) => {
    const asset = item.generation?.assets[0];
    if (!asset?.url) return;
    publishAssets.push({
      asset: {
        slug: asset.slug,
        url: asset.url,
        media_type: asset.media_type,
      },
      ...(item.vendor_product_id
        ? { vendor_product_id: item.vendor_product_id }
        : {}),
      // Note: no vendor_variant_id is required, and we don't have that data available, so omitting for now
    });
  });
  return publishAssets;
}
